import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import type { ApiPage } from "../lib/api";

type Props = {
  pages: ApiPage[];
  activePath: string | null;
  mode: "owner" | "share";
  onSelect: (page: ApiPage) => void;
  // Owner-only callbacks. Returning Promise<unknown> lets callers pass
  // hooks that return the created/updated resource without an adapter.
  onAddPage?: (input: { name: string; path: string }) => Promise<unknown>;
  onRenamePage?: (pageId: string, name: string) => Promise<unknown>;
  onDeletePage?: (pageId: string) => Promise<unknown>;
  onReorderPages?: (pageIds: string[]) => Promise<unknown>;
  onSetPageVisibility?: (pageId: string, hidden: boolean) => Promise<unknown>;
};

type DropTarget = { overId: string; position: "before" | "after" };

// Vertical pages list rendered in the project's left sidebar. Visual
// language matches the PageTabs strip it replaces: active item gets a
// black background, badge shows open-comment count, owners can rename
// in place / delete on hover / add via inline composer / reorder via
// drag-and-drop.
export function PagesSidebar({
  pages,
  activePath,
  mode,
  onSelect,
  onAddPage,
  onRenamePage,
  onDeletePage,
  onReorderPages,
  onSetPageVisibility,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  const canDrag = mode === "owner" && !!onReorderPages && pages.length > 1;

  function onDragStart(e: DragEvent<HTMLLIElement>, pageId: string) {
    if (!canDrag || renamingId) {
      e.preventDefault();
      return;
    }
    setDragId(pageId);
    e.dataTransfer.effectAllowed = "move";
    // Some browsers need data set on the transfer object for drag to fire
    // at all; the value is unused, we read from local state instead.
    try {
      e.dataTransfer.setData("text/plain", pageId);
    } catch {
      // ignore — Safari can be picky during synthetic events
    }
  }

  function onDragOver(e: DragEvent<HTMLLIElement>, pageId: string) {
    if (!canDrag || !dragId) return;
    e.preventDefault();
    if (pageId === dragId) {
      // Dragging over yourself — no indicator.
      setDropTarget(null);
      return;
    }
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const isBefore = e.clientY < rect.top + rect.height / 2;
    const position: "before" | "after" = isBefore ? "before" : "after";
    if (
      dropTarget?.overId !== pageId ||
      dropTarget.position !== position
    ) {
      setDropTarget({ overId: pageId, position });
    }
  }

  function onDragLeaveRow(e: DragEvent<HTMLLIElement>, pageId: string) {
    if (dropTarget?.overId === pageId) {
      // Only clear if the cursor actually left this row's box. Without
      // this check, dragover on a child triggers leave on the parent and
      // the indicator flickers.
      const rect = e.currentTarget.getBoundingClientRect();
      const { clientX: x, clientY: y } = e;
      if (
        x < rect.left ||
        x > rect.right ||
        y < rect.top ||
        y > rect.bottom
      ) {
        setDropTarget((prev) => (prev?.overId === pageId ? null : prev));
      }
    }
  }

  function onDrop(e: DragEvent<HTMLLIElement>) {
    if (!canDrag || !dragId || !dropTarget) return;
    e.preventDefault();
    const newOrder = computeNewOrder(pages, dragId, dropTarget);
    setDragId(null);
    setDropTarget(null);
    // Don't await — the hook handles optimistic state + rollback on
    // failure. The list already reflects the new order client-side.
    void onReorderPages?.(newOrder);
  }

  function onDragEnd() {
    setDragId(null);
    setDropTarget(null);
  }

  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
        Pages
      </div>
      <ul className="mt-2 space-y-0.5">
        {pages.map((page) => {
          const isActive = page.path === activePath;
          const isRenaming = renamingId === page.id;
          const isDragging = dragId === page.id;
          const showDropBefore =
            dropTarget?.overId === page.id && dropTarget.position === "before";
          const showDropAfter =
            dropTarget?.overId === page.id && dropTarget.position === "after";
          return (
            <li
              key={page.id}
              draggable={canDrag && !isRenaming}
              onDragStart={(e) => onDragStart(e, page.id)}
              onDragOver={(e) => onDragOver(e, page.id)}
              onDragLeave={(e) => onDragLeaveRow(e, page.id)}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              className={
                "group relative " +
                (canDrag && !isRenaming ? "cursor-grab " : "") +
                (isDragging ? "opacity-40 " : "")
              }
            >
              {showDropBefore ? <DropLine /> : null}
              {isRenaming && mode === "owner" && onRenamePage ? (
                <RenameField
                  initial={page.name}
                  onSubmit={async (next) => {
                    if (next && next !== page.name) await onRenamePage(page.id, next);
                    setRenamingId(null);
                  }}
                  onCancel={() => setRenamingId(null)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => onSelect(page)}
                  onDoubleClick={() => mode === "owner" && setRenamingId(page.id)}
                  title={mode === "owner" ? "Drag to reorder · Double-click to rename" : page.fullUrl}
                  className={
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition " +
                    (isActive
                      ? "bg-neutral-900 text-white"
                      : page.hidden
                        ? "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                        : "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900")
                  }
                >
                  <span
                    className={
                      "min-w-0 flex-1 truncate font-medium " +
                      (page.hidden && !isActive ? "italic" : "")
                    }
                  >
                    {page.name}
                  </span>
                  {page.openCommentCount > 0 ? (
                    <span
                      className={
                        "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold " +
                        (isActive
                          ? "bg-white/20 text-white"
                          : "bg-neutral-200 text-neutral-700")
                      }
                    >
                      {page.openCommentCount}
                    </span>
                  ) : null}
                  {mode === "owner" && onSetPageVisibility ? (
                    <span
                      role="button"
                      aria-label={
                        page.hidden
                          ? `Show "${page.name}" to collaborators`
                          : `Hide "${page.name}" from collaborators`
                      }
                      title={
                        page.hidden
                          ? "Hidden from collaborators — click to show"
                          : "Visible to collaborators — click to hide"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        void onSetPageVisibility(page.id, !page.hidden);
                      }}
                      className={
                        "ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition " +
                        // Hidden pages: always visible affordance so the
                        // owner can find them. Visible pages: only show
                        // on row hover (or focus) to keep the strip clean.
                        (page.hidden
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100") +
                        " " +
                        (isActive
                          ? "text-white hover:bg-white/20"
                          : "text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900")
                      }
                    >
                      {page.hidden ? <EyeOffIcon /> : <EyeIcon />}
                    </span>
                  ) : null}
                  {mode === "owner" && onDeletePage && pages.length > 1 ? (
                    <span
                      role="button"
                      aria-label={`Delete ${page.name}`}
                      title="Delete page"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          confirm(
                            `Delete page "${page.name}"? Comments on this URL stay accessible under "other pages".`,
                          )
                        ) {
                          void onDeletePage(page.id);
                        }
                      }}
                      className={
                        "invisible ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] group-hover:visible " +
                        (isActive
                          ? "bg-white/20 text-white hover:bg-red-500"
                          : "bg-neutral-200 text-neutral-700 hover:bg-red-500 hover:text-white")
                      }
                    >
                      ×
                    </span>
                  ) : null}
                </button>
              )}
              {showDropAfter ? <DropLine /> : null}
            </li>
          );
        })}
      </ul>

      {mode === "owner" && onAddPage ? (
        adding ? (
          <AddPageInline
            onSubmit={async (input) => {
              await onAddPage(input);
              setAdding(false);
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="mt-1 flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-left text-xs text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
          >
            <span aria-hidden>+</span>
            <span>Add page</span>
          </button>
        )
      ) : null}
    </div>
  );
}

function DropLine() {
  return (
    <div
      aria-hidden
      className="pointer-events-none mx-1 my-0.5 h-0.5 rounded-full"
      style={{ background: "#1AE07D" }}
    />
  );
}

function computeNewOrder(
  pages: ApiPage[],
  dragId: string,
  target: DropTarget,
): string[] {
  const ids = pages.map((p) => p.id).filter((id) => id !== dragId);
  const overIdx = ids.indexOf(target.overId);
  if (overIdx === -1) return pages.map((p) => p.id);
  const insertAt = target.position === "before" ? overIdx : overIdx + 1;
  ids.splice(insertAt, 0, dragId);
  return ids;
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M10.6 6.1A10.5 10.5 0 0112 6c6.5 0 10 7 10 7a17.7 17.7 0 01-3.7 4.4M6.6 6.6A17.7 17.7 0 002 12s3.5 7 10 7c1.5 0 2.8-.3 4-.8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9.9 9.9a3 3 0 004.2 4.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RenameField({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: string;
  onSubmit: (next: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void onSubmit(value.trim());
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  }

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => void onSubmit(value.trim())}
      onKeyDown={onKey}
      maxLength={120}
      className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs focus:border-neutral-900 focus:outline-none"
    />
  );
}

function AddPageInline({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: { name: string; path: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [path, setPath] = useState("/");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await onSubmit({ name: name.trim(), path: path.trim() });
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) setError("A page already exists for that path.");
      else if (status === 400) setError("Path must start with `/`.");
      else setError("Couldn’t add page.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-1 space-y-1 rounded-md bg-neutral-100 p-2"
    >
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Page name"
        maxLength={120}
        required
        className="w-full rounded border-0 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-neutral-900"
      />
      <input
        value={path}
        onChange={(e) => setPath(e.target.value)}
        placeholder="/path"
        maxLength={2048}
        required
        className="w-full rounded border-0 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-neutral-900"
      />
      {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
      <div className="flex justify-end gap-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 text-[11px] text-neutral-500 hover:text-neutral-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-neutral-900 px-2 py-0.5 text-[11px] text-white hover:bg-neutral-700 disabled:opacity-50"
        >
          {busy ? "…" : "Add"}
        </button>
      </div>
    </form>
  );
}
